import { useNavigate } from "react-router-dom";
import { Video, X } from "lucide-react";
import api from "../api";
import { playAnnouncementChime } from "../utils/notificationSound";
import { showDesktopNotification } from "../utils/desktopNotify";
import Portal from "./common/Portal";

// A patient is waiting in an online session the doctor hasn't joined
// (backend meeting/recording/recording_consent.py::alert_doctor_if_patient_waits).
// The Alerts bell alone was too easy to miss, so it's also a card on
// screen with a tone, until the doctor joins or dismisses it. Only recent,
// unread alerts show; the tone plays once per alert even though every page
// mounts its own layout (module-level set).
const RECENT_MS = 15 * 60 * 1000;
const chimed = new Set();
// The Appointments page is /appointment; alerts saved before this fix say /appointments.
const target = (n) => (!n.link || n.link === "/appointments" ? "/appointment" : n.link);

export default function PatientWaitingPopup({ alerts, onChange }) {
  const navigate = useNavigate();
  const now = Date.now();
  const waiting = (alerts?.items || []).filter(
    (n) => n.type === "patient_waiting" && !n.read && n.created_at && now - Date.parse(n.created_at) < RECENT_MS
  );
  if (!waiting.length) return null;
  waiting.forEach((n) => {
    if (!chimed.has(n.id)) {
      chimed.add(n.id);
      playAnnouncementChime();
      // Also a system notification, for a doctor who isn't looking at this tab.
      showDesktopNotification({
        title: n.title,
        body: n.body || "Click to start the consultation.",
        tag: `patient-waiting-${n.id}`,
        onClick: () => navigate(target(n)),
      });
    }
  });

  const close = (n) => api.markNotificationRead(n.id).catch(() => {}).finally(() => onChange && onChange());
  const join = (n) => {
    close(n);
    navigate(target(n));
  };

  return (
    <Portal>
      <div className="fixed bottom-4 right-4 z-[70] flex w-[calc(100%-2rem)] max-w-sm flex-col gap-2">
        {waiting.map((n) => (
          <div key={n.id} role="alert" className="rounded-2xl border border-emerald-200 bg-white p-4 shadow-2xl">
            <div className="flex items-start gap-3">
              <span className="relative mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                <Video size={18} />
                <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 animate-ping" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-secondary-900">{n.title}</p>
                {n.body && <p className="mt-0.5 text-xs text-secondary-600">{n.body}</p>}
                <div className="mt-3 flex gap-2">
                  <button type="button" onClick={() => join(n)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700">
                    <Video size={14} /> Start consultation
                  </button>
                  <button type="button" onClick={() => close(n)}
                    className="rounded-lg border border-secondary-200 px-3 py-1.5 text-xs font-semibold text-secondary-700 hover:bg-secondary-50">
                    Dismiss
                  </button>
                </div>
              </div>
              <button type="button" onClick={() => close(n)} aria-label="Dismiss" className="rounded-full p-1 text-secondary-400 hover:bg-secondary-100">
                <X size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </Portal>
  );
}
